import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2, BarChart3, Euro, CheckCircle2, ClipboardCheck, Download, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageLoading } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";

const eur = (n) => (n == null ? "—" : "€" + Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const pct = (m) => (m == null ? "—" : (m * 100).toFixed(1) + "%");
const h = (n) => (n == null ? "—" : Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const monthEnd = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); };

export default function TimeFinancials() {
  const { t } = useTranslation();
  const [prof, setProf] = useState(null);
  const [finance, setFinance] = useState(true);
  const [rates, setRates] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [subs, setSubs] = useState([]);

  const loadProf = useCallback(() => {
    api.get("/time/financials/baustellen").then((r) => setProf(r.data))
      .catch((e) => { if (e.response?.status === 403) { setFinance(false); setProf({ rows: [], totals: {} }); } });
  }, []);
  const loadRates = useCallback(() => { api.get("/time/rates").then((r) => setRates(r.data.rates)).catch(() => setRates([])); }, []);
  const loadApprovals = useCallback(() => { api.get("/time/approvals").then((r) => setApprovals(r.data.approvals || [])).catch(() => {}); }, []);
  useEffect(() => {
    loadProf(); loadRates(); loadApprovals();
    api.get("/time/workers").then((r) => setWorkers(r.data.workers || [])).catch(() => {});
    api.get("/time/sites").then((r) => setSites(r.data.sites || [])).catch(() => {});
    api.get("/time/subcontractors").then((r) => setSubs(r.data.subcontractors || [])).catch(() => {});
  }, [loadProf, loadRates, loadApprovals]);

  if (!prof || !rates) return <PageLoading />;

  return (
    <div data-testid="time-financials">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Link to="/app/employer/time" className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="fin-back"><ArrowLeft className="h-4 w-4" /> {t("fin.back")}</Link>
        <h1 className="ml-2 font-heading text-2xl font-extrabold text-navy">{t("fin.title")}</h1>
      </div>

      <Approvals list={approvals} workers={workers} sites={sites} subs={subs} t={t} reload={loadApprovals} />

      {finance ? <>
        <Profitability prof={prof} t={t} />
        <Rates rates={rates} workers={workers} sites={sites} subs={subs} t={t} reload={loadRates} />
        <Settlements t={t} />
      </> : <div className="mb-8 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500" data-testid="fin-nofinance">{t("fin.noPerm")}</div>}
    </div>
  );
}

function ScopePicker({ scope, setScope, scopeId, setScopeId, workers, sites, subs, t, includeOrg }) {
  const targets = scope === "WORKER" ? workers.map((w) => ({ id: w.worker_id, label: `${w.name} · ${w.worker_id}` }))
    : scope === "SITE" ? sites.map((s) => ({ id: s.id, label: s.name }))
      : scope === "SUBCONTRACTOR" ? subs.map((s) => ({ id: s.id, label: s.name })) : [];
  return (
    <>
      <select value={scope} onChange={(e) => { setScope(e.target.value); setScopeId(""); }} className="rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="appr-scope">
        {includeOrg && <option value="ORG">{t("appr.scopeOrg")}</option>}
        <option value="WORKER">{t("appr.scopeWorker")}</option>
        <option value="SUBCONTRACTOR">{t("appr.scopeSub")}</option>
        <option value="SITE">{t("appr.scopeSite")}</option>
      </select>
      {scope !== "ORG" && (
        <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="appr-target">
          <option value="">—</option>
          {targets.map((tg) => <option key={tg.id} value={tg.id}>{tg.label}</option>)}
        </select>
      )}
    </>
  );
}

function Approvals({ list, workers, sites, subs, t, reload }) {
  const [scope, setScope] = useState("SUBCONTRACTOR");
  const [scopeId, setScopeId] = useState("");
  const [ptype, setPtype] = useState("MONTHLY");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(monthEnd());
  const [busy, setBusy] = useState(false);

  const submit = async (action) => {
    setBusy(true);
    try {
      const r = await api.post("/time/approvals", { scope, scope_id: scope === "ORG" ? null : scopeId, period_type: ptype, period_from: from, period_to: to, action });
      toast.success(`${t("appr.done")} · ${r.data.hours} h`);
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const reopen = async (id) => { try { await api.post(`/time/approvals/${id}/reopen`); reload(); } catch { toast.error("Failed"); } };
  const badge = (s) => ({ APPROVED: "bg-emerald-100 text-emerald-700", CORRECTION_REQUIRED: "bg-amber-100 text-amber-700", REOPENED: "bg-slate-100 text-slate-600" }[s] || "bg-slate-100 text-slate-600");

  return (
    <section className="mb-10">
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy"><ClipboardCheck className="h-5 w-5 text-cyan-dark" /> {t("appr.title")}</h2>
      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="appr-form">
        <ScopePicker scope={scope} setScope={setScope} scopeId={scopeId} setScopeId={setScopeId} workers={workers} sites={sites} subs={subs} t={t} includeOrg />
        <select value={ptype} onChange={(e) => setPtype(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="appr-period">
          <option value="DAILY">{t("appr.daily")}</option>
          <option value="WEEKLY">{t("appr.weekly")}</option>
          <option value="MONTHLY">{t("appr.monthly")}</option>
          <option value="SUBCONTRACTOR_PERIOD">{t("appr.subPeriod")}</option>
        </select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" data-testid="appr-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" data-testid="appr-to" />
        <Button onClick={() => submit("approve")} disabled={busy} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="appr-approve">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1 h-4 w-4" /> {t("appr.approve")}</>}</Button>
        <Button onClick={() => submit("request_correction")} disabled={busy} variant="outline" className="rounded-full" data-testid="appr-correction">{t("appr.requestCorrection")}</Button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="appr-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["scopeWorker", "period", "hours", "shifts", "status", "approvedBy"].map((c) => <th key={c} className="px-4 py-3">{t(`appr.${c === "scopeWorker" ? "target" : c}`)}</th>)}<th /></tr>
          </thead>
          <tbody>
            {list.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-slate-400">{t("appr.empty")}</td></tr> :
              list.map((a) => (
                <tr key={a.id} className="border-b border-slate-50" data-testid={`appr-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{a.label}</td>
                  <td className="px-4 py-3 text-slate-500">{a.period_from} → {a.period_to}</td>
                  <td className="px-4 py-3 text-slate-600">{h(a.hours)}</td>
                  <td className="px-4 py-3 text-slate-600">{a.shift_count}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge(a.status)}`}>{a.status}</span></td>
                  <td className="px-4 py-3 text-slate-500">{a.approved_by_name}</td>
                  <td className="px-4 py-3 text-right">{a.status !== "REOPENED" && <button onClick={() => reopen(a.id)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-navy" data-testid={`appr-reopen-${a.id}`}><RotateCcw className="h-3.5 w-3.5" /> {t("appr.reopen")}</button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Profitability({ prof, t }) {
  const cols = ["colSite", "colTotalH", "colIntH", "colSubH", "colLaborCost", "colSubCost", "colRevenue", "colContribution", "colMargin"];
  const to = prof.totals;
  return (
    <section className="mb-10">
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy"><BarChart3 className="h-5 w-5 text-cyan-dark" /> {t("fin.profitability")}</h2>
      {prof.missing_rate && <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="fin-missing-rate">{t("fin.missingRate")}</div>}
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="fin-profit-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{cols.map((c) => <th key={c} className="px-4 py-3">{t(`fin.${c}`)}</th>)}</tr>
          </thead>
          <tbody>
            {prof.rows.length === 0 ? <tr><td colSpan={cols.length} className="p-8 text-center text-slate-400">{t("fin.none")}</td></tr> :
              prof.rows.map((r) => (
                <tr key={r.site_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`fin-row-${r.site_id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{r.site}</td>
                  <td className="px-4 py-3 text-slate-600">{h(r.total_hours)}</td>
                  <td className="px-4 py-3 text-slate-600">{h(r.internal_hours)}</td>
                  <td className="px-4 py-3 text-slate-600">{h(r.sub_hours)}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(r.labor_cost)}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(r.subcontractor_cost)}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(r.revenue)}</td>
                  <td className={`px-4 py-3 font-semibold ${(r.contribution || 0) < 0 ? "text-red-600" : "text-emerald-600"}`}>{eur(r.contribution)}</td>
                  <td className="px-4 py-3 font-medium text-navy">{pct(r.margin)}</td>
                </tr>
              ))}
          </tbody>
          {prof.rows.length > 0 && (
            <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-navy">
              <tr data-testid="fin-totals">
                <td className="px-4 py-3">{t("fin.totals")}</td>
                <td className="px-4 py-3">{h(to.total_hours)}</td><td className="px-4 py-3">{h(to.internal_hours)}</td><td className="px-4 py-3">{h(to.sub_hours)}</td>
                <td className="px-4 py-3">{eur(to.labor_cost)}</td><td className="px-4 py-3">{eur(to.subcontractor_cost)}</td><td className="px-4 py-3">{eur(to.revenue)}</td>
                <td className={`px-4 py-3 ${(to.contribution || 0) < 0 ? "text-red-600" : "text-emerald-600"}`}>{eur(to.contribution)}</td><td className="px-4 py-3">{pct(to.margin)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function Rates({ rates, workers, sites, subs, t, reload }) {
  const [scope, setScope] = useState("ORG");
  const [scopeId, setScopeId] = useState("");
  const [cost, setCost] = useState("");
  const [billing, setBilling] = useState("");
  const [wage, setWage] = useState("");
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const targets = scope === "WORKER" ? workers.map((w) => ({ id: w.worker_id, label: `${w.name} · ${w.worker_id}` }))
    : scope === "SITE" ? sites.map((s) => ({ id: s.id, label: s.name }))
      : scope === "SUBCONTRACTOR" ? subs.map((s) => ({ id: s.id, label: s.name })) : [];
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/time/rates", { scope, scope_id: scope === "ORG" ? null : scopeId,
        cost_rate: cost === "" ? null : Number(cost), billing_rate: billing === "" ? null : Number(billing),
        wage_rate: wage === "" ? null : Number(wage), valid_from: from });
      toast.success(t("fin.save") + " ✓");
      setCost(""); setBilling(""); setWage(""); setScopeId(""); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const del = async (id) => { try { await api.delete(`/time/rates/${id}`); reload(); } catch { toast.error("Failed"); } };
  return (
    <section className="mb-10">
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy"><Euro className="h-5 w-5 text-cyan-dark" /> {t("fin.rates")}</h2>
      <div className="mb-5 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-6" data-testid="fin-rate-form">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">{t("fin.scope")}</label>
          <select value={scope} onChange={(e) => { setScope(e.target.value); setScopeId(""); }} className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="fin-scope">
            <option value="ORG">{t("fin.scopeOrg")}</option><option value="WORKER">{t("fin.scopeWorker")}</option><option value="SITE">{t("fin.scopeSite")}</option><option value="SUBCONTRACTOR">{t("fin.scopeSub")}</option>
          </select>
        </div>
        {scope !== "ORG" && (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">{t("fin.target")}</label>
            <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="fin-target">
              <option value="">—</option>{targets.map((tg) => <option key={tg.id} value={tg.id}>{tg.label}</option>)}
            </select>
          </div>
        )}
        <div><label className="mb-1 block text-xs font-medium text-slate-500">{t("fin.costRate")}</label><Input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="fin-cost" /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">{t("fin.billingRate")}</label><Input type="number" min="0" step="0.01" value={billing} onChange={(e) => setBilling(e.target.value)} data-testid="fin-billing" /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">{t("fin.wageRate")}</label><Input type="number" min="0" step="0.01" value={wage} onChange={(e) => setWage(e.target.value)} data-testid="fin-wage" /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">{t("fin.validFrom")}</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="fin-from" /></div>
        <div className="flex items-end lg:col-span-6"><Button onClick={save} disabled={busy} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="fin-save-rate">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> {t("fin.save")}</>}</Button></div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="fin-rates-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["colScope", "colTarget", "colCost", "colBilling", "colWage", "colFrom", "colUntil", ""].map((c, i) => <th key={i} className="px-4 py-3">{c ? t(`fin.${c}`) : ""}</th>)}</tr>
          </thead>
          <tbody>
            {rates.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-400">{t("fin.empty")}</td></tr> :
              rates.map((r) => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`fin-rate-${r.id}`}>
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{t(`fin.scope${r.scope === "ORG" ? "Org" : r.scope === "WORKER" ? "Worker" : r.scope === "SITE" ? "Site" : "Sub"}`)}</span></td>
                  <td className="px-4 py-3 text-slate-600">{r.label}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(r.cost_rate)}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(r.billing_rate)}</td>
                  <td className="px-4 py-3 text-slate-600">{eur(r.wage_rate)}</td>
                  <td className="px-4 py-3 text-slate-500">{r.valid_from || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.valid_until || "—"}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600" data-testid={`fin-del-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Settlements({ t }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(monthEnd());
  const [rows, setRows] = useState(null);
  const load = async () => {
    try { const r = await api.get(`/time/settlements/subcontractors?period_from=${from}&period_to=${to}`); setRows(r.data.rows); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const download = async (subId, fmt) => {
    try {
      const res = await api.get(`/time/settlements/subcontractors/${subId}/export?period_from=${from}&period_to=${to}&format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `abrechnung_${subId}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy"><Euro className="h-5 w-5 text-cyan-dark" /> {t("settle.title")}</h2>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" data-testid="settle-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" data-testid="settle-to" />
        <Button onClick={load} variant="outline" className="rounded-full" data-testid="settle-load">{t("settle.load")}</Button>
      </div>
      <p className="mb-2 text-xs text-slate-400">{t("settle.note")}</p>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="settle-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["sub", "approvedHours", "rate", "amount"].map((c) => <th key={c} className="px-4 py-3">{t(`settle.${c}`)}</th>)}<th /></tr>
          </thead>
          <tbody>
            {rows == null ? <tr><td colSpan={5} className="p-6 text-center text-slate-400">…</td></tr> :
              rows.length === 0 ? <tr><td colSpan={5} className="p-6 text-center text-slate-400">{t("settle.empty")}</td></tr> :
                rows.map((r) => (
                  <tr key={r.subcontractor_id} className="border-b border-slate-50" data-testid={`settle-row-${r.subcontractor_id}`}>
                    <td className="px-4 py-3 font-semibold text-navy">{r.subcontractor}</td>
                    <td className="px-4 py-3 text-slate-600">{h(r.approved_hours)}</td>
                    <td className="px-4 py-3 text-slate-600">{r.rate == null ? t("settle.mixed") : eur(r.rate)}</td>
                    <td className="px-4 py-3 font-semibold text-navy">{eur(r.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => download(r.subcontractor_id, "pdf")} className="mr-3 text-xs text-cyan-dark hover:underline" data-testid={`settle-pdf-${r.subcontractor_id}`}><Download className="mr-0.5 inline h-3.5 w-3.5" />{t("settle.pdf")}</button>
                      <button onClick={() => download(r.subcontractor_id, "xlsx")} className="text-xs text-cyan-dark hover:underline" data-testid={`settle-xlsx-${r.subcontractor_id}`}><Download className="mr-0.5 inline h-3.5 w-3.5" />{t("settle.xlsx")}</button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
